<template lang="pug">
  yz-card.px-6.py-5(color="white")
    .yz-heading-8 {{ $t('panel.summary') }}

    // Row 1: Personal Information
    v-row.mt-5(no-gutters, justify="space-between")
      // Billed to
      v-col
        .yz-heading-9 {{ $t('panel.billed_to') }}

        template(v-if="isBilledToEmpty")
          .yz-regular-body-sm.text-neutral-500.mt-1 {{ $t('na') }}

        template(v-else)
          .d-flex.align-center.mt-1(v-if="summaryData.billedTo.name")
            yz-icon.mr-1(size="16px" color="yz-neutral-500") $yzi-user-02
            .yz-heading-9.text-neutral-500 {{ summaryData.billedTo.name }}
            yz-badge.ml-1(v-if="summaryData.billedTo.relationship" border, border-color="#0C6DFF", radius="12px" text-color="#0C6DFF" height="16px")
              span.yz-medium-body-2xs {{ summaryData.billedTo.relationship }}

          .yz-regular-body-sm.mt-1(v-if="summaryData.billedTo.staff_id") {{ summaryData.billedTo.staff_id }}

          .d-flex.align-center.mt-1(v-if="summaryData.billedTo.phone")
            .yz-link-body-sm {{ summaryData.billedTo.phone }}
            yz-icon.ml-1.cursor-pointer(size="16px" color="yz-neutral-500" @click="onOpenWhatsAppBilledTo") mdi-whatsapp

      // Patient details
      v-col
        .yz-heading-9 {{ $t('panel.patient_details') }}

        template(v-if="isPatientDetailsEmpty")
          .yz-regular-body-sm.text-neutral-500.mt-1 {{ $t('na') }}

        template(v-else)
          .d-flex.align-center.mt-1(v-if="summaryData.patientDetails.name")
            yz-icon.mr-1(size="16px" color="yz-neutral-500") $yzi-user-02
            .yz-heading-9.text-neutral-500 {{ summaryData.patientDetails.name }}

          .yz-regular-body-sm.mt-1(v-if="summaryData.patientDetails.nric") {{ summaryData.patientDetails.nric }}

          .yz-regular-body-sm.mt-1 {{ $t('panel.chit_no') }} :&nbsp;
            span(v-if="summaryData.patientDetails.chitNo") {{ summaryData.patientDetails.chitNo }}
            span.text-neutral-500(v-else) {{ $t('na') }}

      // Person in charge
      v-col
        .yz-heading-9 {{ $t('panel.person_in_charge') }}

        template(v-if="isPersonInChargeEmpty")
          .yz-regular-body-sm.text-neutral-500.mt-1 {{ $t('na') }}

        template(v-else)
          .d-flex.align-center.mt-1(v-if="summaryData.personInCharge.name")
            yz-icon.mr-1(size="16px" color="yz-neutral-500") $yzi-user-02
            .yz-heading-9.text-neutral-500 {{ summaryData.personInCharge.name }}

          .d-flex.align-center.mt-1(v-if="summaryData.personInCharge.phone")
            .yz-link-body-sm {{ summaryData.personInCharge.phone }}
            yz-icon.ml-1.cursor-pointer(size="16px" color="yz-neutral-500" @click="onOpenWhatsAppPIC") mdi-whatsapp

    // Row 2: Claim Details
    v-row.mt-5(no-gutters)
      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.panel_claim_number') }}
        .yz-regular-body-sm.mt-1
          span(v-if="summaryData.claimNumber") {{ summaryData.claimNumber }}
          span.text-neutral-500(v-else) {{ $t('na') }}

      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.id') }}
        .yz-regular-body-sm.mt-1
          span(v-if="summaryData.id") {{ summaryData.id }}
          span.text-neutral-500(v-else) {{ $t('na') }}

      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.created_date') }}
        .yz-regular-body-sm.mt-1
          span(v-if="summaryData.createdDate") {{ summaryData.createdDate }}
          span.text-neutral-500(v-else) {{ $t('na') }}

    // Row 3: Additional Details
    v-row.mt-5(no-gutters)
      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.panel_claim_submission_due_date') }}
        .yz-regular-body-sm.mt-1
          submission-due-date-text(:panel-data="parentClaim.panel")

      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.received_date') }}
        .yz-regular-body-sm.mt-1
          span(v-if="summaryData.receivedDate") {{ summaryData.receivedDate }}
          span.text-neutral-500(v-else) {{ $t('na') }}

      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.verification_link') }}
        .d-flex.align-center.mt-1(v-if="summaryData.verificationLink")
          .yz-link-body-sm.truncate {{ summaryData.verificationLink }}
          yz-icon.ml-1.cursor-pointer(size="16px" color="yz-neutral-500" @click="onClickVerificationLink") $yzi-share-03
        .yz-regular-body-sm.text-neutral-500.mt-1(v-else) {{ $t('na') }}

    // Invoice Items Section
    .d-flex.justify-space-between.align-center.mt-6
      .yz-medium-body-sm {{ $t('panel.invoice_items') }}
      yz-btn(:label="$t('panel.view_invoice')" size="sm" secondary, @click="onViewInvoice")

    // Data Table
    yz-data-table.mt-3(:headers="invoiceHeaders" :items="invoiceItems" :items-per-page="pageSize" :page="currentPage" @update:options="onOptionsUpdate")
      template(v-slot:item.name="{ item }")
        .yz-medium-body-sm {{ item.name }}

      template(v-slot:item.rate="{ item }")
        .yz-medium-body-sm {{ item.rate | displayPrice($currency) }}

      template(v-slot:item.qty="{ item }")
        .yz-medium-body-sm.text-center {{ item.qty }}

      template(v-slot:item.total="{ item }")
        .yz-medium-body-sm.text-right {{ item.total | displayPrice($currency) }}

      template(v-slot:footer)
        yz-divider.my-0
        // Pagination and total summary
        .d-flex.justify-space-between.align-center.py-2.px-3
          .yz-regular-body-xs.no-wrap
            | {{ $t('panel.total') }}:
            span.yz-medium-body-xs.ml-1 {{ totalCount }} {{ $t('panel.items') }}
          yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalCount" type="item" arrow)

    // Totals
    v-row.mt-5(justify="end", no-gutters)
      v-col(cols="3")
        v-row(no-gutters)
          v-col(cols="8")
            .yz-regular-body-sm {{ $t('panel.invoice_total') }}
          v-col(cols="4")
            .yz-regular-body-sm.text-right {{ invoiceTotal | displayPrice($currency) }}

        yz-divider.my-1

        v-row(no-gutters)
          v-col(cols="8")
            .yz-regular-body-sm {{ $t('panel.claimed_amount') }}
          v-col(cols="4")
            .yz-regular-body-sm.text-right {{ claimedAmount | displayPrice($currency) }}

        // Additional financial fields (shown when status is not 'submitted')
        template(v-if="showAdditionalFinancials")
          yz-divider.my-1

          v-row(no-gutters)
            v-col(cols="8")
              .yz-regular-body-sm {{ $t('panel.rejected_amount') }}
            v-col(cols="4")
              .yz-regular-body-sm.text-right {{ -rejectedAmount | displayPrice($currency) }}

          yz-divider.my-1

          v-row(no-gutters)
            v-col(cols="8")
              .yz-regular-body-sm {{ $t('admin_fee') }}
            v-col(cols="4")
              .yz-regular-body-sm.text-right {{ -adminFee | displayPrice($currency) }}

          yz-divider.my-1

          v-row(no-gutters)
            v-col(cols="8")
              .yz-regular-body-sm {{ $t('panel.approved_amount') }}
            v-col(cols="4")
              .yz-regular-body-sm.text-right {{ approvedAmount | displayPrice($currency) }}

          yz-divider.my-1

          v-row(no-gutters)
            v-col(cols="8")
              .yz-regular-body-sm {{ $t('panel.total_received') }}
            v-col(cols="4")
              .yz-regular-body-sm.text-right.text-success-500 {{ totalReceived | displayPrice($currency) }}

        yz-divider.mt-1.mb-2

        v-row(no-gutters)
          v-col(cols="8")
            .yz-heading-9 {{ $t('panel.amount_due') }}
          v-col(cols="4")
            .yz-heading-8.text-right {{ amountDue | displayPrice($currency) }}
</template>

<script>
import { DateTime } from 'luxon'
import SubmissionDueDateText from '@/components/clinic/panel/claims/SubmissionDueDateText.vue'

export default {
  components: { SubmissionDueDateText },
  props: {
    parentClaim: {
      type: Object,
      default: null
    },
    parentInvoice: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      // Summary data
      summaryData: {
        billedTo: { name: null, staff_id: null, phone: null, relationship: null },
        patientDetails: { name: null, nric: null, chitNo: null },
        personInCharge: { name: null, phone: null },
        claimNumber: null,
        id: null,
        receivedDate: null,
        createdDate: null,
        verificationLink: null
      },
      // Invoice items
      invoiceItems: [],
      // Totals
      invoiceTotal: 0,
      claimedAmount: 0,
      rejectedAmount: 0,
      adminFee: 0,
      approvedAmount: 0,
      totalReceived: 0,
      amountDue: 0,
      // Table configuration
      invoiceHeaders: [
        { text: this.$t('panel.item_name'), value: 'name', width: '50%' },
        { text: this.$t('panel.rate'), value: 'rate', align: 'right' },
        { text: this.$t('panel.qty'), value: 'qty', align: 'center' },
        { text: this.$t('panel.total'), value: 'total', align: 'right' }
      ],
      currentPage: 1,
      pageSize: 10,
      // Track sort state to detect changes
      sortBy: [],
      sortDesc: []
    }
  },
  computed: {
    totalCount() {
      return this.invoiceItems.length
    },
    isBilledToEmpty() {
      const { name, staff_id, phone } = this.summaryData.billedTo
      return !name && !staff_id && !phone
    },
    isPatientDetailsEmpty() {
      const { name, nric, chitNo } = this.summaryData.patientDetails
      return !name && !nric && !chitNo
    },
    isPersonInChargeEmpty() {
      const { name, phone } = this.summaryData.personInCharge
      return !name && !phone
    },
    showAdditionalFinancials() {
      return this.parentClaim?.status !== 'submitted'
    }
  },
  mounted() {
    this.mapData()
  },
  methods: {
    mapData() {
      // Return early if props are not available
      if (!this.parentInvoice || !this.parentClaim) return

      // Map summary data
      this.summaryData = {
        billedTo: {
          name: this.parentInvoice.staff_name,
          staff_id: this.parentInvoice.staff_id,
          phone: this.parentInvoice.staff_phone,
          relationship: this.parentInvoice.relationship
        },
        patientDetails: {
          name: this.parentInvoice.name,
          nric: this.parentInvoice.ic,
          chitNo: this.parentInvoice.chit_number
        },
        personInCharge: {
          name: this.parentClaim.panel?.pic_name,
          phone: this.parentClaim.panel?.phone
        },
        claimNumber: this.parentClaim.rid,
        id: this.parentClaim.id,
        receivedDate: this.parentClaim.received_date ? this.formatDate(this.parentClaim.received_date) : null,
        createdDate: this.parentClaim.created ? this.formatDate(this.parentClaim.created) : null,
        verificationLink: this.parentClaim.panel.verification_link || null
      }

      // Map invoice items
      this.invoiceItems = []
      if (this.parentInvoice.treatments && Array.isArray(this.parentInvoice.treatments)) {
        this.parentInvoice.treatments.forEach(treatment => {
          // Validate required treatment fields
          if (!treatment || !treatment.name) return

          // Treatment item
          const price = treatment.price || 0
          const quantity = treatment.quantity || 1

          this.invoiceItems.push({
            name: treatment.name,
            rate: price,
            qty: quantity,
            total: price * quantity
          })
        })
      }

      // Map totals
      this.invoiceTotal = this.parentClaim.invoice_total || 0
      this.claimedAmount = this.parentClaim.submitted_amount || 0
      this.rejectedAmount = this.parentClaim.rejected_amount || 0
      this.adminFee = this.parentClaim.admin_fee || 0
      this.approvedAmount = this.parentClaim.approved_amount || 0
      this.totalReceived = this.parentClaim.received_amount || 0
      this.amountDue = this.parentClaim.due_amount || 0
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = DateTime.fromISO(dateString)
        return date.toFormat('d MMMM yyyy')
      } catch (error) {
        console.error('Error formatting date:', error)
        return 'N/A'
      }
    },
    onClickVerificationLink() {
      if (this.summaryData.verificationLink) {
        window.open(this.summaryData.verificationLink, '_blank')
      }
    },
    onViewInvoice() {
      if (!this.parentInvoice?.lead_pid) return

      const route = this.$router.resolve(`/admin/clinic/checkout/${this.parentInvoice.lead_pid}`)
      window.open(route.href, '_blank')
    },
    onOpenWhatsAppBilledTo() {
      this.openWhatsApp(this.summaryData.billedTo.phone)
    },
    onOpenWhatsAppPIC() {
      this.openWhatsApp(this.summaryData.personInCharge.phone)
    },
    openWhatsApp(phoneNumber) {
      if (!phoneNumber) return

      const sanitizedPhone = this.$options.filters.phoneSanitized(phoneNumber)
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${sanitizedPhone}`
      window.open(whatsappUrl, '_blank')
    },
    onOptionsUpdate(options) {
      // Check if sorting has changed
      const sortByChanged = JSON.stringify(options.sortBy) !== JSON.stringify(this.sortBy)
      const sortDescChanged = JSON.stringify(options.sortDesc) !== JSON.stringify(this.sortDesc)

      if (sortByChanged || sortDescChanged) {
        // Reset to first page when sorting changes
        this.currentPage = 1
        // Update tracked sort state
        this.sortBy = options.sortBy
        this.sortDesc = options.sortDesc
      }
    }
  }
}
</script>

<style scoped></style>
